import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function VoucherProductManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('voucherProduct:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const voucherProductSchema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    description: {
      label: 'Beschreibung',
      api: { itemField: 'description' },
      type: 'text',
      required: false,
      minWidth: '300px',
      previewInList: true,
      previewMaxLines: 3,
      useHyphens: true,
    },
    branchId: {
      label: 'Filiale',
      type: 'select',
      api: {
        fieldName: 'branch',
        itemField: 'branchId',
        idName: '_id',
        resource: 'branch',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Filiale auswählen',
      required: true,
      minWidth: '180px',
    },
    isActive: {
      label: 'Aktiv',
      api: { itemField: 'isActive' },
      type: 'boolean',
      required: false,
      minWidth: '120px',
    },
    minValue: {
      label: 'Mindestwert',
      api: { itemField: 'minValue' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    maxValue: {
      label: 'Maximalwert',
      api: { itemField: 'maxValue' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    defaultValue: {
      label: 'Standardwert',
      api: { itemField: 'defaultValue' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    suggestedValues: {
      label: 'Vorschlagswerte (kommagetrennt)',
      api: { itemField: 'suggestedValues' },
      type: 'array',
      placeholder: 'z. B. 25, 50, 100',
      displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => Number.parseFloat(String(v).trim()))
          .filter(v => !Number.isNaN(v)),
      listDisplayValue: value => (Array.isArray(value) && value.length ? value.join(', ') : '—'),
      minWidth: '240px',
    },
    allowCustomValue: {
      label: 'Freier Betrag erlaubt',
      api: { itemField: 'allowCustomValue' },
      type: 'boolean',
      required: false,
      minWidth: '200px',
    },
    buyableFrom: {
      label: 'Kaufbar ab',
      api: { itemField: 'buyableFrom' },
      type: 'date',
      required: false,
      minWidth: '160px',
    },
    buyableTo: {
      label: 'Kaufbar bis',
      api: { itemField: 'buyableTo' },
      type: 'date',
      required: false,
      minWidth: '160px',
    },
    validityType: {
      label: 'Gültigkeitstyp',
      api: { itemField: 'validityType' },
      type: 'select',
      options: [
        { value: 'german3years', label: 'DE: 3 Jahre' },
        { value: 'custom', label: 'Benutzerdefiniert' },
      ],
      required: true,
      minWidth: '180px',
    },
    discounts: {
      label: 'Rabattstufen (threshold:discount; …)',
      api: { itemField: 'discounts' },
      type: 'text',
      placeholder: 'z. B. 50:10;100:20',
      displayValue: value =>
        Array.isArray(value) && value.length
          ? value.map(v => `${v.threshold}:${v.discount}`).join('; ')
          : '—',
      transformValue: value => {
        const s = String(value || '').trim();
        if (!s) return [];
        return s
          .split(';')
          .map(p => p.trim())
          .filter(Boolean)
          .map(pair => {
            const [t, d] = pair.split(':').map(x => Number.parseFloat(String(x).trim()));
            if (!Number.isFinite(t) || !Number.isFinite(d)) return null;
            return { threshold: t, discount: d };
          })
          .filter(Boolean);
      },
      minWidth: '260px',
    },
    benefitMode: {
      label: 'Vorteilsmodus',
      api: { itemField: 'benefitMode' },
      type: 'select',
      options: [
        { value: 'none', label: 'Keiner' },
        { value: 'percent', label: 'Prozent-Rabatt' },
        { value: 'bonus', label: '€-Bonus' },
      ],
      required: false,
      minWidth: '180px',
    },
    bonusTiers: {
      label: 'Bonusstufen (threshold:bonus; …)',
      api: { itemField: 'bonusTiers' },
      type: 'text',
      placeholder: 'z. B. 50:10;100:30',
      displayValue: value =>
        Array.isArray(value) && value.length
          ? value.map(v => `${v.threshold}:${v.bonus}`).join('; ')
          : '—',
      transformValue: value => {
        const s = String(value || '').trim();
        if (!s) return [];
        return s
          .split(';')
          .map(p => p.trim())
          .filter(Boolean)
          .map(pair => {
            const [t, b] = pair.split(':').map(x => Number.parseFloat(String(x).trim()));
            if (!Number.isFinite(t) || !Number.isFinite(b)) return null;
            return { threshold: t, bonus: b };
          })
          .filter(Boolean);
      },
      minWidth: '260px',
    },
    maxStock: {
      label: 'Max. Bestand',
      api: { itemField: 'maxStock' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    soldStock: {
      label: 'Verkauft',
      api: { itemField: 'soldStock' },
      type: 'number',
      required: false,
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '120px',
    },
    redemptionConditions: {
      label: 'Einlösebedingungen',
      type: 'object',
      api: { itemField: 'redemptionConditions', skipSpecial: true },
      schema: {
        minPurchaseAmount: { label: 'Mindestkaufbetrag', type: 'number', required: false },
        maxUsagePerTransaction: {
          label: 'Max. Nutzung pro Kauf',
          type: 'number',
          required: false,
        },
        restrictedBranches: {
          label: 'Beschränkte Filialen',
          type: 'multi-select',
          api: {
            fieldName: 'branch',
            itemField: 'restrictedBranches',
            idName: '_id',
            resource: 'branch',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Filialen auswählen',
          required: false,
        },
        restrictedEntertainments: {
          label: 'Beschränkte Entertainments',
          type: 'multi-select',
          api: {
            fieldName: 'entertainment',
            itemField: 'restrictedEntertainments',
            idName: '_id',
            resource: 'entertainment',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Entertainments auswählen',
          required: false,
        },
        restrictedEntertainmentTypes: {
          label: 'Beschränkte Entertainment-Typen (kommagetrennt)',
          type: 'array',
          placeholder: 'z. B. axezone, bashzone',
          displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => String(v).trim())
              .filter(Boolean),
        },
      },
      displayValue: value => {
        if (!value || typeof value !== 'object') return '—';
        const parts = [];
        if (value.minPurchaseAmount) parts.push(`Mindestkauf: ${value.minPurchaseAmount}`);
        if (value.maxUsagePerTransaction)
          parts.push(`Max/Nutzung: ${value.maxUsagePerTransaction}`);
        if (Array.isArray(value.restrictedBranches) && value.restrictedBranches.length)
          parts.push(`Filialen: ${value.restrictedBranches.length}`);
        if (Array.isArray(value.restrictedEntertainments) && value.restrictedEntertainments.length)
          parts.push(`Entertainments: ${value.restrictedEntertainments.length}`);
        if (
          Array.isArray(value.restrictedEntertainmentTypes) &&
          value.restrictedEntertainmentTypes.length
        )
          parts.push(`Typen: ${value.restrictedEntertainmentTypes.join(', ')}`);
        return parts.join(' | ') || '—';
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={voucherProductSchema}
      title='Neues Gutscheinprodukt anlegen'
      resourceType='voucherProduct'
      headerSearchPlaceholder='Gutschein-Produkte durchsuchen'
      parentIsEditable={authCtx.checkRight('voucherProduct:update')}
      options={{
        titleKey: 'name',
      }}
    />
    </Box>
  );
}

export default VoucherProductManagement;
